/**
 * GamifiedPath.tsx
 * ----------------
 * Displays the user's progress in a gamified format.
 * - Shows step progress, points, and earned badges.
 * - Progress description adapts to current step and language.
 * - Integrates with ProgressContext and localStorage.
 */

import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useProgress } from '../context/ProgressContext';
import ProgressBar from './ProgressBar';
import Button from './common/Button';
import Box from './common/Box';

const GamifiedPath = () => {
  const { setStep, language } = useProgress();
  const lang = (['en', 'fi', 'sv'].includes(language) ? language : 'en') as 'en' | 'fi' | 'sv';
  const history = useHistory();
  useEffect(() => {
    setStep(2); // Mark as on gamified path
    console.log('latestCompletedStep here it is !!    ' + step);
  }, [setStep]);
  const step = Number(localStorage.getItem('latestCompletedStep')) || 0;

  const t = {
    intro: {
      en: (
        <>
          Welcome to your gamified learning path!
          <br />
          Here you can track your progress, earn points, and unlock achievements as you complete the
          tutorial.
        </>
      ),
      fi: (
        <>
          Tervetuloa pelillistetylle oppimispolullesi!
          <br />
          Täällä voit seurata edistymistäsi, ansaita pisteitä ja avata saavutuksia suorittaessasi
          opasta.
        </>
      ),
      sv: (
        <>
          Välkommen till din spelifierade inlärningsväg!
          <br />
          Här kan du följa dina framsteg, samla poäng och låsa upp prestationer när du slutför
          handledningen.
        </>
      ),
    },
    progress: {
      en: 'Your Progress',
      fi: 'Edistymisesi',
      sv: 'Din framsteg',
    },
    points: {
      en: 'Points:',
      fi: 'Pisteet:',
      sv: 'Poäng:',
    },
    badges: {
      en: 'Badges Earned:',
      fi: 'Ansaitut merkit:',
      sv: 'Förtjänade märken:',
    },
    none: {
      en: 'None',
      fi: 'Ei yhtään',
      sv: 'Inga',
    },
    back: {
      en: 'Back to Crash Course',
      fi: 'Takaisin kurssin yleisnäkymään',
      sv: 'Tillbaka till kursöversikten',
    },
    descriptionIfNoProgress: {
      en: (step: number) =>
        step === 0 ? 'You have not made any progress yet' : `You are on step: ${step}`,
      fi: (step: number) => (step === 0 ? 'Et ole vielä edistynyt' : `Olet vaiheessa: ${step}`),
      sv: (step: number) =>
        step === 0 ? 'Du har inte gjort några framsteg' : `Du är på steg: ${step}`,
    },
    descriptionIfProgress: {
      en: (step: number) => `You are on step: ${step}`,
      fi: (step: number) => `Olet vaiheessa: ${step}`,
      sv: (step: number) => `Du är på steg: ${step}`,
    },
    descriptionIfReady: {
      en: (step: number) => 'You have completed all steps!',
      fi: (step: number) => 'Olet suorittanut kaikki vaiheet!',
      sv: (step: number) => 'Du har slutfört alla steg!',
    },
    part1Badge: {
      en: 'Beginner',
      fi: 'Aloittelija',
      sv: 'Nybörjare',
    },
    part2Badge: {
      en: 'Sauna Pro',
      fi: 'Saunamestari',
      sv: 'Saunaproffs',
    },
    part3Badge: {
      en: 'Climate Ready',
      fi: 'Sääolosuhteisiin Valmis',
      sv: 'Klimatbered',
    },
    part4Badge: {
      en: 'Ultra-Ready',
      fi: 'Täysin Valmis',
      sv: 'Fullt Förberedd',
    },

    descriptionSlideOneBadge: {
      en: 'Step 1 learning slide done',
      fi: 'Vaiheen 1 oppimisdia suoritettu',
      sv: 'Steg 1 inlärningsbild klar',
    },
    descriptionSlideTwoBadge: {
      en: 'Step 2 learning slide done',
      fi: 'Vaiheen 2 oppimisdia suoritettu',
      sv: 'Steg 2 inlärningsbild klar',
    },
    descriptionSlideThreeBadge: {
      en: 'Step 3 learning slide done',
      fi: 'Vaiheen 3 oppimisdia suoritettu',
      sv: 'Steg 3 inlärningsbild klar',
    },
    descriptionSlideFourBadge: {
      en: 'Step 4 learning slide done',
      fi: 'Vaiheen 4 oppimisdia suoritettu',
      sv: 'Steg 4 inlärningsbild klar',
    },
  };

  const earnedBadges = [];
  if (localStorage.getItem('partOneDone') === 'true') {
    earnedBadges.push({
      name: t.part1Badge[lang],
      icon: '🚀',
      description: t.descriptionSlideOneBadge[lang],
    });
  }
  if (localStorage.getItem('partTwoDone') === 'true') {
    earnedBadges.push({
      name: t.part2Badge[lang],
      icon: '♨️',
      description: t.descriptionSlideTwoBadge[lang],
    });
  }
  if (localStorage.getItem('partThreeDone') === 'true') {
    earnedBadges.push({
      name: t.part3Badge[lang],
      icon: '☃️',
      description: t.descriptionSlideThreeBadge[lang],
    });
  }
  if (localStorage.getItem('partFourDone') === 'true') {
    earnedBadges.push({
      name: t.part4Badge[lang],
      icon: '🏆',
      description: t.descriptionSlideFourBadge[lang],
    });
  }

  const progressDescription = (step: number) => {
    if (step === 0) {
      return t.descriptionIfNoProgress[lang](step);
    } else if (step === 4) {
      return t.descriptionIfReady[lang](step);
    } else {
      return t.descriptionIfProgress[lang](step);
    }
  };

  return (
    <Box className="m-4 sm:gap-6 gap-12 ">
      <p className="text-purple-600 text-center font-semibold text-2xl">
        {(() => {
          return progressDescription(step == 0 ? 0 : step);
        })()}
      </p>
      {step == 0 || step == 4 ? (
        ''
      ) : (
        <ProgressBar
          colorClass="bg-purple-600"
          progress={(() => {
            return step / 4;
          })()}
        />
      )}
      <p
        className="text-lg text-purple-600 text-center 
          max-w-xs font-medium"
      >
        {t.intro[lang]}
      </p>
      <Box className='justify-evenly gap-3'
      itemsCenter={false}>
          <h2 className="text-2xl font-semibold text-purple-600">{t.progress[lang]}</h2>
          <p>
            {t.points[lang]}
            <span className="font-bold ml-2">{Number(localStorage.getItem('points'))}</span>
          </p>
          <p>
            {t.badges[lang]}
            <span className="font-bold ml-2">
              {earnedBadges.length === 0
                ? t.none[lang]
                : earnedBadges.map((badge, idx) => (
                    <span
                      key={badge.name}
                      className="inline-flex items-center mr-2"
                      title={badge.description}
                    >
                      <span className="text-xl mr-1">{badge.icon}</span>
                      <span className="text-sm font-semibold">{badge.name}</span>
                    </span>
                  ))}
            </span>
          </p>
      </Box>
      <Button
        gradient={false}
        className="bg-gradient-to-r from-green-600 to-green-500 
          hover:from-green-700 hover:to-green-600
          flex-col flex self-center"
        onClick={() => history.push('/crash-course')}
      >
        {t.back[lang]}
      </Button>
    </Box>
  );
};

export default GamifiedPath;
